import TextInput from "@common/controls/Inputs/Text/component";
import { type WithOnChange, type WithValue } from "@common/declarations";
import withDropdownHOC, { type WithDropdownToggleProps } from "@common/hocs/withDropdown";
import withFocusBlurHOC, { type FocusBlurSharedProps } from "@common/hocs/withFocusBlur";
import withValidateHOC, { type WithValidateCmpProps } from "@common/hocs/withValidate";
import withValueHOC from "@common/hocs/withValue";
import { parseColor } from "@common/lib/color";
import loadable from "@loadable/component";
import { type ColorResult, rgbaToHexa } from "@uiw/color-convert";
import { type SketchProps } from "@uiw/react-color-sketch";
import classnames from "classnames";
import * as React from "react";

import "./style.less";

export { type ColorResult };

const SketchPicker = loadable(() => import(/* webpackChunkName: "react-color-sketch" */ "@uiw/react-color-sketch"), {
  fallback: <div style={{ padding: 16 }}>Loading...</div>,
  resolveComponent: (x) => x.default
});

type ColorPickerPropsBase = WithValue<string> &
  WithOnChange<string> &
  FocusBlurSharedProps & {
    className?: string;
    dropdownClassName?: string;
    isDisabled?: boolean;
    presetColors?: string[];

    onClose?: () => void;
  };

const ColorPickerToggle = React.memo((props: WithDropdownToggleProps & ColorPickerPropsBase) => (
  <>
    <TextInput
      className={props.className}
      isDisabled={props.isDisabled}
      value={props.value}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      onChange={props.onChange}
    />

    <div
      ref={props.toggleRef}
      className={classnames("input-group-addon", { disabled: props.isDisabled })}
      onClick={props.isDisabled ? undefined : props.toggleOpen}
    >
      <i style={{ backgroundColor: props.value }} />
    </div>
  </>
));

export type ColorPickerComponentProps = Omit<SketchProps, "color"> & {
  color?: string;
};
export const ColorPickerComponent = (props: ColorPickerComponentProps) => {
  const color = React.useMemo(() => {
    const res = parseColor(props.color);

    return res ? rgbaToHexa(res) : undefined;
  }, [props.color]);

  return <SketchPicker {...props} color={color} />;
};

const ColorPickerContent = (props: ColorPickerPropsBase) => {
  const onChange = React.useCallback(
    (color: ColorResult) => {
      props.onChange(color.hexa);
    },
    [props.onChange]
  );

  return (
    <ColorPickerComponent
      className={props.dropdownClassName}
      presetColors={props.presetColors}
      color={props.value}
      onChange={onChange}
    />
  );
};

const ColorPickerWithDropdown = withDropdownHOC<ColorPickerPropsBase>(ColorPickerToggle, ColorPickerContent);

export type ColorPickerProps = ColorPickerPropsBase & {
  onBlurValid?: () => void; // if blurs with a valid value
};

class ColorPickerWithValidateBase extends React.PureComponent<ColorPickerProps & WithValidateCmpProps<string>> {
  public render() {
    const { dropdownClassName, isValid, ...props } = this.props;
    return (
      <div className={classnames("colorpicker input-group", { invalid: !isValid })}>
        <ColorPickerWithDropdown
          {...props}
          dropdownClassName={classnames("colorpicker_dropdown", dropdownClassName)}
          onBlur={this._onBlur}
        />
      </div>
    );
  }

  private _onBlur = () => {
    if (this.props.onBlur) this.props.onBlur();
    if (this.props.isValid && this.props.onBlurValid) {
      this.props.onBlurValid();
    }
  };
}
const ColorPickerBase = withValidateHOC<string, WithDropdownToggleProps & ColorPickerProps>(
  ColorPickerWithValidateBase as any, // TODO: why an error w/o any?
  (color) => !!parseColor(color)
);

const ColorPicker = withFocusBlurHOC<ColorPickerProps>(withValueHOC<string, ColorPickerProps>(ColorPickerBase));
export default ColorPicker;
